import { MessageRole, type OpenXAgentMessage } from '@openx/components-core/lib/AiChat/types';
import type { TroubleshootingMessage } from '@openx/components-deal/lib/DealDetails/components/DealTroubleshootingMessageActions';
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpenXAgentToolData } from '../../../context/OpenXAgentContext';
import { useOpenXAgentContext } from '../../../context/useOpenXAgentContext';
import { assembleContextAlertMessage } from '../utils/assembleContextAlertMessage';

type Persona = {
  id: string;
  status: 'matching' | 'non-matching';
  title: string;
  description: string;
  reasons: string;
};

type TargetingSummaryGroup = {
  id: string;
  label: string;
  items: { id: string; label: string; value: string | null; status: 'include' | 'exclude' }[];
};

const targetingSummarySampleMessage = `Targeting Check summary

What you clicked:

\`\`\`openx-widget
summary-1
\`\`\`

You shall target the following:

\`\`\`openx-widget
targeting-1
\`\`\`

You shall NOT target the following:

\`\`\`openx-widget
targeting-2
\`\`\`


End of message.`;

const matchingPersonas: Persona[] = [
  {
    id: 'persona-001',
    status: 'matching',
    title: 'Peter, 32',
    description: 'Miami, Florida – Browsing espn.com on Samsung Galaxy',
    reasons: 'US Geo, Mobile Web Channel, Sports Category, Age 25-45',
  },
  {
    id: 'persona-002',
    status: 'matching',
    title: 'Danielle, 27',
    description: 'Chicago, Illinois – Browsing bleacherreport.com on iPhone 15',
    reasons: 'US Geo, Mobile Web Channel, Sports Category, Age 25-45',
  },
  {
    id: 'persona-003',
    status: 'matching',
    title: 'Marcus, 41',
    description: 'Denver, Colorado – Browsing cbssports.com on Google Pixel 8',
    reasons: 'US Geo, Mobile Web Channel, Sports Category, Age 25-45',
  },
];

const nonMatchingPersonas: Persona[] = [
  {
    id: 'persona-004',
    status: 'non-matching',
    title: 'Sophie, 29',
    description: 'Toronto, Ontario – Browsing tsn.ca on iPhone 15',
    reasons: 'Mobile Excluded by Country (Canada not in Target Geo: US).',
  },
  {
    id: 'persona-005',
    status: 'non-matching',
    title: 'Robert, 58',
    description: 'Austin, Texas – Browsing cnn.com on Dell Desktop',
    reasons:
      'Desktop Excluded by Channel (Target: Mobile Web), News Category not in Target (Sports), Age 58 outside Target Range (25-45).',
  },
  {
    id: 'persona-006',
    status: 'non-matching',
    title: 'Emily, 19',
    description: 'Seattle, Washington – Browsing espn.com on iPad Air',
    reasons: 'Tablet Excluded by Channel (Target: Mobile Web), Age 19 outside Target Range (25-45).',
  },
];

const sampleTargetingSummary: TargetingSummaryGroup[] = [
  {
    id: 'core',
    label: 'Core',
    items: [
      { id: 'format', label: 'Format', value: 'Banner', status: 'include' },
      { id: 'channel', label: 'Channel', value: 'Web & App', status: 'include' },
      { id: 'device', label: 'Device', value: 'Desktop & Mobile', status: 'include' },
    ],
  },
  {
    id: 'quality_safety',
    label: 'Quality & Safety',
    items: [
      { id: 'mfa', label: 'MFA Excluded', value: null, status: 'exclude' },
      { id: 'fraud', label: 'Fraud Excluded', value: null, status: 'exclude' },
      { id: 'high_attention', label: 'High Attention Segment Enabled', value: null, status: 'include' },
    ],
  },
  {
    id: 'audience',
    label: 'Audience',
    items: [
      { id: 'country', label: 'Country', value: 'United States', status: 'include' },
      { id: 'audience_segment', label: 'Audience', value: 'Sports Enthusiasts', status: 'exclude' },
      { id: 'gender', label: 'Gender', value: 'All', status: 'include' },
      { id: 'language', label: 'Language', value: 'English', status: 'include' },
      { id: 'age', label: 'Age', value: '25-50', status: 'include' },
    ],
  },
  {
    id: 'advanced',
    label: 'Advanced',
    items: [
      { id: 'iab_category', label: 'IAB Category', value: 'Pets (IAB16)', status: 'include' },
      { id: 'domains', label: 'Domains', value: '10', status: 'include' },
      { id: 'app_bundles', label: 'App Bundles', value: '30', status: 'include' },
      { id: 'viewability', label: 'Viewability', value: '>50%', status: 'exclude' },
      { id: 'os', label: 'OS', value: 'iOS, Android', status: 'include' },
    ],
  },
];

const THINKING_STEPS = ['Analyzing request...', 'Finding solution...', 'Preparing your answer...'];
const STEP_DELAY = 1000;

// AiChat keys messages by timestamp, so mocked timestamps must be unique even within the same millisecond
const nextTimestamp = (lastTimestampRef: RefObject<number>) => {
  lastTimestampRef.current = Math.max(Date.now(), lastTimestampRef.current + 1);

  return String(lastTimestampRef.current);
};

type MockedAgentProps = {
  userName: string;
};

export const useMockedAgent = ({ userName }: MockedAgentProps) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<OpenXAgentMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingProcessMessages, setThinkingProcessMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // a ref, not state: rapid consecutive sends must see the update before the next render
  const isFirstAgentMessageRef = useRef(true);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const lastTimestampRef = useRef(0);

  const previousToolDataRef = useRef<OpenXAgentToolData | null | undefined>(undefined);
  const { activeTool } = useOpenXAgentContext();
  const welcomeMessage: OpenXAgentMessage = useMemo(() => {
    const now = nextTimestamp(lastTimestampRef);

    return {
      role: MessageRole.WELCOMING,
      text: `### ${t('Hi, {name}!', { name: userName })} \n ${t('What are we working on today? Select or search your Deal ID below:')}`,
      timestamp: now,
      invocation_id: now,
    };
  }, [userName, t]);
  const getDealContextAlertMessage = useCallback(
    (toolData: OpenXAgentToolData | null): OpenXAgentMessage | null => {
      const text = assembleContextAlertMessage(t, toolData);

      if (!text) return null;

      const now = nextTimestamp(lastTimestampRef);

      return {
        role: MessageRole.ALERT,
        text,
        timestamp: now,
        invocation_id: now,
      };
    },
    [t]
  );

  const schedule = useCallback((callback: () => void, delay: number) => {
    timeoutIdsRef.current.push(setTimeout(callback, delay));
  }, []);

  // send mock targeting summary as first agent response, then proceed with mimicking user's messages
  const getSummaryMessages = useCallback((): TroubleshootingMessage[] => {
    const timestamp = nextTimestamp(lastTimestampRef);
    // widgets share the invocation_id of the agent message they belong to
    const invocationId = timestamp;

    return [
      {
        role: MessageRole.AGENT,
        text: targetingSummarySampleMessage,
        timestamp,
        invocation_id: invocationId,
        langfuse_trace_id: `sample-trace-${timestamp}`,
      },
      {
        role: MessageRole.WIDGET,
        author: 'assistant',
        timestamp,
        invocation_id: invocationId,
        widget: { id: 'summary-1', provider: 'targeting-summary', payload: { groups: sampleTargetingSummary } },
      },
      {
        role: MessageRole.WIDGET,
        author: 'assistant',
        timestamp,
        invocation_id: invocationId,
        widget: { id: 'targeting-1', provider: 'targeting-personas', payload: { personas: matchingPersonas } },
      },
      {
        role: MessageRole.WIDGET,
        author: 'assistant',
        timestamp,
        invocation_id: invocationId,
        widget: { id: 'targeting-2', provider: 'targeting-personas', payload: { personas: nonMatchingPersonas } },
      },
    ];
  }, []);

  const getQueriedMessage = (query: string): TroubleshootingMessage => {
    const timestamp = nextTimestamp(lastTimestampRef);

    return {
      role: MessageRole.AGENT,
      text: `You are absolutely right! ${query}`,
      timestamp,
      invocation_id: timestamp,
      langfuse_trace_id: `sample-trace-${timestamp}`,
    };
  };

  const sendSummaryMessage = useCallback(() => {
    setIsThinking(true);
    isFirstAgentMessageRef.current = false;

    THINKING_STEPS.forEach((_, i) => {
      schedule(() => setThinkingProcessMessages(THINKING_STEPS.slice(0, i + 1)), (i + 1) * STEP_DELAY);
    });

    schedule(
      () => {
        const replies = getSummaryMessages();
        setMessages(prev => [...prev, ...replies]);
        setThinkingProcessMessages([]);
        setIsThinking(false);
      },
      (THINKING_STEPS.length + 1) * STEP_DELAY
    );
  }, [schedule, getSummaryMessages]);

  const sendMessage = (query: string) => {
    const now = nextTimestamp(lastTimestampRef);
    setMessages(prev => [...prev, { role: MessageRole.USER, text: query, timestamp: now, invocation_id: now }]);
    // enter the thinking state synchronously so the input is disabled before the first thinking step
    setIsThinking(true);

    const isFirstAgentMessage = isFirstAgentMessageRef.current;
    isFirstAgentMessageRef.current = false;

    THINKING_STEPS.forEach((_, i) => {
      schedule(() => setThinkingProcessMessages(THINKING_STEPS.slice(0, i + 1)), (i + 1) * STEP_DELAY);
    });

    schedule(
      () => {
        const replies = isFirstAgentMessage ? getSummaryMessages() : [getQueriedMessage(query)];
        setMessages(prev => [...prev, ...replies]);
        setThinkingProcessMessages([]);
        setIsThinking(false);
      },
      (THINKING_STEPS.length + 1) * STEP_DELAY
    );
  };

  const clearConversation = useCallback(() => {
    timeoutIdsRef.current.forEach(clearTimeout);
    isFirstAgentMessageRef.current = true;
    setThinkingProcessMessages([]);
    setIsThinking(false);
    setIsLoading(true);
    setMessages([]);
  }, []);

  const showWelcomeMessages = useCallback(
    (toolData: OpenXAgentToolData | null) => {
      timeoutIdsRef.current.push(
        setTimeout(() => {
          const dealContextAlertMessage = getDealContextAlertMessage(toolData);

          setIsLoading(false);
          // keep a deal context picked before the greeting arrived
          setMessages(prev => [welcomeMessage, ...(dealContextAlertMessage ? [dealContextAlertMessage] : []), ...prev]);

          if (toolData) {
            sendSummaryMessage();
          }
        }, STEP_DELAY * 2)
      );
    },
    [welcomeMessage, getDealContextAlertMessage, sendSummaryMessage]
  );

  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current;

    return () => timeoutIds.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const previousToolData = previousToolDataRef.current;
    previousToolDataRef.current = activeTool.toolData;

    // a deal picked inside the chat keeps the greeting on screen; any other context change starts fresh
    if (previousToolData === null && activeTool.toolData) {
      const dealContextAlertMessage = getDealContextAlertMessage(activeTool.toolData);

      if (dealContextAlertMessage) setMessages(prev => [...prev, dealContextAlertMessage]);
      sendSummaryMessage();
      return;
    }

    clearConversation();
    showWelcomeMessages(activeTool.toolData);
  }, [clearConversation, showWelcomeMessages, getDealContextAlertMessage, sendSummaryMessage, activeTool.toolData]);

  return {
    messages,
    isThinking,
    isLoading,
    sendMessage,
    thinkingProcessMessages,
  };
};
